/**
 * Die Detailansicht einer Floskel (FR-SET-03).
 *
 * Hier hoert das Spiel auf, ein Spiel zu sein. Vier Dinge stehen drin, und
 * die Reihenfolge ist die Aussage:
 *
 * 1. **Im Klartext** — was wirklich gemeint ist.
 * 2. **Warum das nicht traegt** — der Gedanke dahinter.
 * 3. **Frag den Vendor** — was ein Einkaeufer damit anfaengt.
 * 4. **Was stattdessen zaehlt** — die Empfehlung. Eine Floskel zu zerlegen
 *    ist leicht; zu sagen, was man statt ihrer verlangen soll, ist die
 *    Arbeit, fuer die jemand CICS bezahlt.
 * 5. **Belege** — worauf sich das stuetzt, mit Quelle und Link.
 *
 * Der vierte Punkt ist der Grund, warum das Ganze einem Haus gehoert, das
 * Intelligence verkauft. Eine Floskel ohne Beleg zu widerlegen waere
 * derselbe Fehler, nur mit anderem Vorzeichen. Wo wir keinen Beleg haben,
 * steht keiner da.
 */
import { useLang } from '../lang'
import type { Term } from '../lib/types'
import { Sheet } from './Sheet'

export function TermSheet({
  term,
  heardToday,
  marked = false,
  autoOpen,
  onAutoOpenChange,
  onUnmark,
  onClose,
}: {
  term: Term
  heardToday?: number
  /** Ist das Feld gerade markiert. Dann steht hier auch der Rueckweg. */
  marked?: boolean
  /** Zeigt sich dieses Blatt nach dem Markieren von selbst. */
  autoOpen?: boolean
  onAutoOpenChange?: (an: boolean) => void
  onUnmark?: () => void
  onClose: () => void
}) {
  const { t, pickText, lang } = useLang()
  const variants = term.variants[lang] ?? term.variants.de ?? []
  const fragen = term.ask?.[lang] ?? term.ask?.de ?? []
  const einzelfrage = pickText(term.counterQuestion)
  const belege = term.evidence ?? []

  return (
    <Sheet title={pickText(term.text)} onClose={onClose}>
      <div className="stack">
        <div>
          <div className="kicker">{t('term.plain')}</div>
          <p style={{ margin: '4px 0 0', fontSize: 17 }}>{pickText(term.plain)}</p>
        </div>

        {pickText(term.why) ? (
          <div>
            <div className="kicker">{t('term.why')}</div>
            <p style={{ margin: '4px 0 0' }}>{pickText(term.why)}</p>
          </div>
        ) : null}

        {variants.length > 0 ? (
          <div>
            <div className="kicker">{t('term.variants')}</div>
            <p className="muted small" style={{ margin: '4px 0 0' }}>{variants.join(' · ')}</p>
          </div>
        ) : null}

        {fragen.length > 0 || einzelfrage ? (
          <div className="banner">
            <div className="kicker">{t('term.question')}</div>
            {fragen.length > 0 ? (
              <ul className="asklist">
                {fragen.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{einzelfrage}</p>
            )}
          </div>
        ) : null}

        {pickText(term.recommend) ? (
          <div className="advice">
            <div className="kicker">{t('term.recommend')}</div>
            <p>{pickText(term.recommend)}</p>
          </div>
        ) : null}

        {belege.length > 0 ? (
          <div>
            <div className="kicker">{t('term.evidence')}</div>
            <ol className="evidence">
              {belege.map((b, i) => (
                <li key={i}>
                  <p>{pickText(b.claim)}</p>
                  {b.url ? (
                    <a href={b.url} target="_blank" rel="noopener noreferrer">
                      {b.cite || b.title} &rarr;
                    </a>
                  ) : (
                    <span className="muted">{b.cite || b.title}</span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <p className="small muted" style={{ margin: 0 }}>{t('term.noEvidence')}</p>
        )}

        <div className="row row--between small muted">
          <span>
            {t('term.grade')}: <b className="pts">{term.grade}</b>
          </span>
          {heardToday ? <span>{t('term.heardToday', { n: heardToday })}</span> : null}
        </div>

        {/* Der Rueckweg steht dort, wo man ihn sucht: im Blatt, das sich
            nach einem Fehlgriff ohnehin oeffnet. */}
        {marked && onUnmark ? (
          <button type="button" className="btn btn--ghost btn--block" onClick={onUnmark}>
            {t('room.unmark')}
          </button>
        ) : null}

        {onAutoOpenChange ? (
          <label className="row small muted" style={{ gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              style={{ width: 18, minHeight: 18 }}
              checked={autoOpen !== false}
              onChange={(e) => onAutoOpenChange(e.target.checked)}
            />
            <span>{t('room.autoInfo')}</span>
          </label>
        ) : null}
      </div>
    </Sheet>
  )
}
